import './ApplicationPrioritizationWizard.scss';

import { AttributeConfigs, CriticalityMap, NumberPartition, StringPartition  } from '@amzn/awscat-portfolio-assessment-types';
import { Alert, Button, Container, ExpandableSection, FormField, Grid, Header, Input, Multiselect, SpaceBetween, Wizard } from '@amzn/awsui-components-react';
import { useRef, useState } from 'react';

import { constants } from '../../../common/Localization';
import { format } from '../../../common/util';
import { AttributeSelectionOption } from './ApplicationPrioritization';
import BusinessRanking from './BusinessRanking';
import { MAX_NUMERIC_PARTITION_BOUNDS, MIN_NUMERIC_PARTITION_BOUNDS } from './constants';

interface Props {
    attrSelectionOptions: AttributeSelectionOption[];
    selectedAttributes: AttributeSelectionOption[];
    cancel: () => void;
    saveAttributeConfig: (attributes: string) => Promise<void>;
    getCriticality: () => Promise<CriticalityMap>;
    calculateRanking: (criticality: string) => Promise<void>;
    loading: boolean;
}

const numericScoringColSpan = [{ colspan: 6 }, { colspan: 4 }, {colspan: 2}];
const numericBoundsColSpan = [{colspan: 3}, {colspan: 3}, {colspan: 3}, {colspan: 3}];
const stringyScoringColSpan = [{ colspan: 4 }, { colspan: 6 }, {colspan: 2}];

const ApplicationPrioritizationWizard = ({ 
    attrSelectionOptions, calculateRanking, cancel, getCriticality, selectedAttributes, loading, saveAttributeConfig 
}: Props) => {
    const [ activeStepIndex, setActiveStepIndex ] = useState(0);
    const [ selectedAttrs, setSelectedAttrs ] = useState<AttributeSelectionOption[]>(selectedAttributes);
    const [ disableSubmitBtn, setDisableSubmitBtn ] = useState(true);
    const [ selectAttributeErrorMsg, setSelectAttributeErrorMsg ] = useState('');
    const [ rankAttributeErrorMsg, setRankAttributeErrorMsg ] = useState('');
    const [ loadingNextStep, setLoadingNextStep ] = useState(false);
    const onSubmit = useRef(() => {});

    const preventNextClick = (): boolean => {
        if (activeStepIndex === 0) {
            if (selectedAttrs.length === 0) {
                setSelectAttributeErrorMsg(constants.APP_PRIORITIZATION_SELECT_ATTRIBUTE_MSG);
                return true;
            }
            if (selectedAttrs.some(attr => (attr.invalidScoreMessage || attr.invalidBoundMessage))) {
                return true; // these errors will display in the expandable sections
            }
        } else if (activeStepIndex === 1) {
            if (loading) {
                return true;
            } else if (disableSubmitBtn) {
                setRankAttributeErrorMsg(constants.APP_PRIORITIZATION_NOT_RANKED_MSG);
                return true;
            }
            return false;
        }
        return false;
    };

    const scoreValidationMsg = (scores: number[]): string => {
        const invalidIndex = scores.findIndex(score => (isNaN(score) || score > 100 || score < 0));
        if (invalidIndex > -1) {
            return format(constants.APP_PRIORITIZATION_EDIT_STEP_1_INVALID_SCORE, [scores[invalidIndex].toString()]);
        }

        return '';
    };

    const handleScoreChange = (attrIndex: number, itemIndex: number, newValue: number, isNumeric: boolean): void => {
        const deepCopiedSelectedAttrs = [...selectedAttrs];
        // Score number update
        const deepCopiedPartition = Object.assign(deepCopiedSelectedAttrs[attrIndex].partition, {});
        if (isNumeric) {
            const newRangeOutput = [...(deepCopiedPartition as NumberPartition).rangeOutputs];
            newRangeOutput[itemIndex] = newValue;
            (deepCopiedPartition as NumberPartition).rangeOutputs = newRangeOutput;

            // Validate score for the whole attribute
            deepCopiedSelectedAttrs[attrIndex].invalidScoreMessage = scoreValidationMsg(newRangeOutput);
        } else {
            const newItems = [...(deepCopiedPartition as StringPartition).items];
            newItems[itemIndex].value = newValue;
            (deepCopiedPartition as StringPartition).items = newItems;

            // Validate score for the whole attribute
            deepCopiedSelectedAttrs[attrIndex].invalidScoreMessage = scoreValidationMsg(newItems.map(item => item.value));
        }

        setSelectedAttrs(deepCopiedSelectedAttrs);
    };

    const boundValidationMsg = (bounds: string[]): string => {
        // Bound number validation, compare each bound to its nearest right neighbor
        for (let index = 0; index < bounds.length - 1; index ++) {
            const left = bounds[index];     
            const right = bounds[index + 1];
            if (isNaN(parseFloat(left)) || isNaN(parseFloat(right))) {
                return constants.APP_PRIORITIZATION_EDIT_STEP_1_NO_NAN;
            }
            if (parseFloat(left) >= parseFloat(right)) {
                return format(constants.APP_PRIORITIZATION_EDIT_STEP_1_INVALID_BOUND, [left, right]);
            }
        }
        return '';
    };

    const handleBoundsChange = (attrIndex: number, boundIndex: number, newUpperBound: number): void => {
        const deepCopiedSelectedAttrs = [...selectedAttrs];
        const deepCopiedPartition = Object.assign(deepCopiedSelectedAttrs[attrIndex].partition, {});
        const deepCopiedUpperBounds = [...(deepCopiedPartition as NumberPartition).upperBounds];

        // Bound number update
        deepCopiedUpperBounds[boundIndex] = newUpperBound.toString();
        deepCopiedSelectedAttrs[attrIndex].invalidBoundMessage = boundValidationMsg(deepCopiedUpperBounds);

        (deepCopiedPartition as NumberPartition).upperBounds = deepCopiedUpperBounds;
        setSelectedAttrs(deepCopiedSelectedAttrs);
    };

    const addAndRemoveBoundsAt = (attrIndex: number, scoreIndex: number, isAdd: boolean): void => {
        const deepCopiedSelectedAttrs = [...selectedAttrs];
        const deepCopiedPartition = Object.assign(deepCopiedSelectedAttrs[attrIndex].partition, {});
        const deepCopiedUpperBounds = [...(deepCopiedPartition as NumberPartition).upperBounds];
        const deepCopiedRangeOutput = [...(deepCopiedPartition as NumberPartition).rangeOutputs];
        if (isAdd) {
            if (deepCopiedRangeOutput.length >= MAX_NUMERIC_PARTITION_BOUNDS) {
                return;
            }
            deepCopiedUpperBounds.splice(scoreIndex, 0, '');
            deepCopiedRangeOutput.splice(scoreIndex, 0, 0);
        } else {
            if (deepCopiedRangeOutput.length <= MIN_NUMERIC_PARTITION_BOUNDS) {
                return;
            }
            deepCopiedUpperBounds.splice(scoreIndex < deepCopiedUpperBounds.length ? scoreIndex : scoreIndex - 1, 1);
            deepCopiedRangeOutput.splice(scoreIndex, 1);
        }
        deepCopiedSelectedAttrs[attrIndex].invalidBoundMessage = boundValidationMsg(deepCopiedUpperBounds);
        (deepCopiedPartition as NumberPartition).upperBounds = deepCopiedUpperBounds;
        (deepCopiedPartition as NumberPartition).rangeOutputs = deepCopiedRangeOutput;
        setSelectedAttrs(deepCopiedSelectedAttrs);
    };

    const commonScoringHeader = (isNumeric: boolean): JSX.Element => (
        <Grid
            gridDefinition={isNumeric ? numericScoringColSpan : stringyScoringColSpan}
        >
            {isNumeric ?
                <div><b>{constants.APP_PRIORITIZATION_VALUE_RANGE}</b> {constants.APP_PRIORITIZATION_VALUE_RANGE_DESCRIPTION}</div> :
                <b>{constants.APP_PRIORITIZATION_VALUE}</b>
            }
            <div><b>{constants.APP_PRIORITIZATION_SCORE}</b> - <i>0-100</i></div>
            <div></div>
        </Grid>
    );

    const buildNumericScoring = (attrIndex: number, partition: NumberPartition) => {
        const upperBounds = partition.upperBounds;
        const rangeScores = partition.rangeOutputs;
        return (
            <div>
                {commonScoringHeader(true)}
                {rangeScores.map((score, index) => {
                    const AddRemoveBounds = (
                        <div>
                            <Button iconName='close' variant='inline-icon' onClick={() => addAndRemoveBoundsAt(attrIndex, index, false)} />
                            <Button iconName='add-plus' variant='inline-icon' onClick={() => addAndRemoveBoundsAt(attrIndex, index, true)} />
                        </div>
                    );
                    let bound;
                    if (index === 0) {
                        bound = (
                            <Grid gridDefinition={numericBoundsColSpan}>
                                <div></div>
                                <div className='bound-text'>&ensp;&ensp;{constants.APP_PRIORITIZATION_VALUE}&ensp;&lt;=</div>
                                <Input type='number' step={1} value={upperBounds[index]} placeholder={constants.APP_PRIORITIZATION_EDIT_STEP_1_MIN}
                                    onChange={({ detail }) => handleBoundsChange(attrIndex, index, parseFloat(detail.value))} />
                                {AddRemoveBounds}
                            </Grid>
                        );
                    } else if (index === rangeScores.length - 1) {
                        bound = (
                            <Grid gridDefinition={numericBoundsColSpan}>
                                <Input type='number' step={1} value={upperBounds[index - 1]} placeholder={constants.APP_PRIORITIZATION_EDIT_STEP_1_MIN}
                                    onChange={({ detail }) => handleBoundsChange(attrIndex, index - 1, parseFloat(detail.value))} />
                                <div className='bound-text'>&lt;&ensp;{constants.APP_PRIORITIZATION_VALUE}</div>
                                <div></div>
                                {AddRemoveBounds}
                            </Grid>
                        );
                    } else {
                        bound = (
                            <Grid gridDefinition={numericBoundsColSpan}>
                                <Input type='number' step={1} value={upperBounds[index - 1]} placeholder={constants.APP_PRIORITIZATION_EDIT_STEP_1_MIN}
                                    onChange={({ detail }) => handleBoundsChange(attrIndex, index - 1, parseFloat(detail.value))} />
                                <div className='bound-text'>&lt;&ensp;{constants.APP_PRIORITIZATION_VALUE}&ensp;&lt;=</div>
                                <Input type='number' step={1} value={upperBounds[index]} placeholder={constants.APP_PRIORITIZATION_EDIT_STEP_1_MAX}
                                    onChange={({ detail }) => handleBoundsChange(attrIndex, index, parseFloat(detail.value))} />
                                {AddRemoveBounds}
                            </Grid>

                        );
                    }
                    return (
                        <Grid key={`numeric-score-${index}`} gridDefinition={numericScoringColSpan}>
                            {bound}
                            <input className='app-prioritization-slider' type='range' min='0' max='100' step='1' value={score}
                                onChange={event => handleScoreChange(attrIndex, index, parseInt(event.target.value), true)} />
                            <Input type='number' inputMode='numeric' step={1} value={score.toString()}
                                onChange={({ detail }) => handleScoreChange(attrIndex, index, parseInt(detail.value), true)} />
                        </Grid>
                    );
                })}
            </div>
        );
    };

    const buildStringyScoring = (attrIndex: number, partition: StringPartition) => {
        return (
            <div>
                {commonScoringHeader(false)}
                {partition.items.map((item, itemIndex) => {
                    return (
                        <Grid key={`string-score-${itemIndex}`} gridDefinition={stringyScoringColSpan}>
                            <div>{item.key}</div>
                            <input className='app-prioritization-slider' type='range' min='0' max='100' step='1' value={item.value}
                                onChange={event => handleScoreChange(attrIndex, itemIndex, parseInt(event.target.value), false)} />
                            <Input type='number' inputMode='numeric' step={1} value={item.value.toString()}
                                onChange={({ detail }) => handleScoreChange(attrIndex, itemIndex, parseInt(detail.value), false)} />
                        </Grid>
                    );
                })}
            </div>
        );
    };

    const saveAttrConfig = async (): Promise<void> => {
        const updatedAttributeConfigObj: AttributeConfigs = {};
        attrSelectionOptions.forEach(attr => {
            if (selectedAttrs.some(selectedAttr => selectedAttr.value === attr.value)) {
                updatedAttributeConfigObj[attr.value] = {
                    id: attr.value,
                    partition: attr.partition,
                    configCompleted: true,
                    selected: true,
                };
            } else {
                updatedAttributeConfigObj[attr.value] = {
                    id: attr.value,
                    partition: attr.partition,
                    configCompleted: false,
                    selected: false,
                };
            }
        });

        await saveAttributeConfig(JSON.stringify(updatedAttributeConfigObj));
    };

    const selectAttributeChange = (selectedOptions: AttributeSelectionOption[]) => {
        setSelectedAttrs(selectedOptions);
        setSelectAttributeErrorMsg('');
    };

    const selectAndScoreContent = (
        <Container
            header={<Header variant="h2">{constants.APP_PRIORITIZATION_EDIT_STEP_1_CONTENT}</Header>}
        >
            <SpaceBetween size="xl">
                <FormField errorText={selectAttributeErrorMsg}>
                    <Multiselect
                        options={attrSelectionOptions}
                        selectedOptions={selectedAttrs}
                        onChange={({ detail }) => selectAttributeChange(detail.selectedOptions as AttributeSelectionOption[])}
                        filteringType="auto"
                        placeholder={constants.APP_PRIORITIZATION_EDIT_STEP_1_SELECT_PLACEHOLDER}
                    />
                </FormField>
                {selectedAttrs.map((selectedAttr, index) => {
                    return (
                        <ExpandableSection
                            key={`selected-attr-scoring-${index}`}
                            variant="container"
                            header={<Header variant="h2">{selectedAttr.label}</Header>}
                            defaultExpanded
                        >
                            <SpaceBetween size="l">
                                {selectedAttr.invalidBoundMessage ? <Alert type="error">{selectedAttr.invalidBoundMessage}</Alert> : null}
                                {selectedAttr.invalidScoreMessage ? <Alert type="error">{selectedAttr.invalidScoreMessage}</Alert> : null}
                                {selectedAttr.partition ?
                                    (('items' in selectedAttr.partition) ?
                                        buildStringyScoring(index, selectedAttr.partition) : buildNumericScoring(index, selectedAttr.partition))
                                    : <Alert>{constants.APP_PRIORITIZATION_EDIT_STEP_1_NO_DATA}</Alert>
                                }
                            </SpaceBetween>
                        </ExpandableSection>
                    );
                })}
            </SpaceBetween>
        </Container>
    );

    const businessRankingContent = (
        <Container>
            <FormField stretch errorText={rankAttributeErrorMsg}>
                <BusinessRanking
                    attributes={selectedAttrs}
                    getCriticality={getCriticality}
                    onSubmit={onSubmit}
                    setDisableSubmitBtn={setDisableSubmitBtn}
                    dismissErrorMsg={() => setRankAttributeErrorMsg('')}
                    calculateRanking={calculateRanking} />
            </FormField>
        </Container>
    );
    
    return (
        <Wizard
            i18nStrings={{
                stepNumberLabel: stepNumber => `${constants.STEP_TEXT} ${stepNumber}`,
                collapsedStepsLabel: (stepNumber, stepsCount) =>
                    `${constants.STEP_TEXT} ${stepNumber} ${constants.OF_TEXT} ${stepsCount}`,
                cancelButton: constants.CANCEL_BUTTON_TEXT,
                previousButton: constants.PREVIOUS_BUTTON_TEXT,
                nextButton: constants.NEXT_BUTTON_TEXT,
                submitButton: constants.APP_PRIORITIZATION_EDIT_SUBMIT_TEXT,
            }}
            onNavigate={({ detail }) => {
                if (detail.reason === 'next') {
                    if (preventNextClick()) {
                        return;
                    }
                    saveAttrConfig();
                }
                setActiveStepIndex(detail.requestedStepIndex);
            } }
            onCancel={cancel}
            onSubmit={() => {
                if (preventNextClick()) {
                    return;
                }
                setLoadingNextStep(true);
                onSubmit.current();
            }}
            activeStepIndex={activeStepIndex}
            isLoadingNextStep={loadingNextStep}
            steps={[
                {
                    title: constants.APP_PRIORITIZATION_EDIT_STEP_1_TITLE,
                    content: selectAndScoreContent,
                },
                {
                    title: constants.APP_PRIORITIZATION_EDIT_STEP_2_TITLE,
                    content: businessRankingContent,
                },
            ]}
        />
    );
};

export default ApplicationPrioritizationWizard;
